/**
 * Filling a registration form from the clinic's own website.
 *
 * The same bargain as 「リンクから追加」 on the fact screens, at the one place it
 * has to work differently: there is no clinic yet, so there is no DRAFT row to
 * approve. What stands in for approval is the form itself - values land in the
 * fields, the operator reads them, corrects them, and presses 登録. Nothing is
 * saved by pressing this button.
 *
 * WHAT EACH VALUE CAME FROM IS SHOWN, not hidden behind a spinner that fills
 * boxes. A pre-filled address is trusted by default, so the sentence it was read
 * out of is printed beside it - that is the only way somebody can tell 「read off
 * the page」 from 「plausible for a clinic in Osaka」, and the server has already
 * discarded anything it could not quote.
 */
import { useState } from 'react';
import { ApiError, post } from '../api.ts';
import { Pill } from './ui.tsx';

interface Suggestion {
  fields: Record<string, string>;
  quotes: Record<string, string>;
  pages: string[];
  dropped: Array<{ field: string; reason: string }>;
}

export function ReadFromSite({
  /** Which form is being filled. Decides which fields are asked for. */
  kind,
  /** The URL to read. Usually the サイトURL field the operator just typed. */
  url,
  /** Field labels, so what came back is named the way the form names it. */
  labels,
  /** Given the values that survived verification, in the form's own field names. */
  onFilled,
}: {
  kind: 'clinic' | 'store';
  url: string;
  labels: Record<string, string>;
  onFilled: (fields: Record<string, string>) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Suggestion | null>(null);

  const ready = /^https:\/\/\S+$/.test(url.trim());

  async function read() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const suggestion = await post<Suggestion>('/admin/site-profile', {
        url: url.trim(),
        kind,
      });
      setResult(suggestion);
      onFilled(suggestion.fields);
    } catch (err) {
      setError(
        err instanceof ApiError ? [err.message, ...err.fields].join(' / ') : '読み取れませんでした',
      );
    } finally {
      setBusy(false);
    }
  }

  const found = result ? Object.keys(result.fields) : [];

  return (
    <div className="field field--full">
      <div className="editpanel__actions" style={{ marginTop: 0 }}>
        <button
          className="btn btn--sm"
          type="button"
          disabled={!ready || busy}
          onClick={() => void read()}
        >
          {busy ? '読み取り中…' : 'サイトから読み取る'}
        </button>
        <span className="muted small">
          {ready
            ? 'ホームページに書かれている内容を下の各項目に入れます。保存はされません。'
            : 'サイトURLを入力すると、ホームページから各項目を読み取れます。'}
        </span>
      </div>

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {result && (
        <div className="panel" style={{ marginTop: 8 }}>
          {found.length === 0 ? (
            <p className="muted small" style={{ margin: 0 }}>
              このページからは読み取れる項目がありませんでした。お手数ですが手入力してください。
              {/*
                Said plainly rather than left as an empty panel: nothing found is
                a real and correct answer for a page that states nothing, and the
                operator needs to know the button worked.
              */}
            </p>
          ) : (
            <>
              <p className="muted small" style={{ marginTop: 0 }}>
                {found.length}件を入力しました。<strong>内容を確認して修正してください。</strong>
                下は、それぞれをページのどこから読み取ったかです。
              </p>
              <ul className="plain small" style={{ margin: 0 }}>
                {found.map((field) => (
                  <li key={field}>
                    <Pill tone="ok">{labels[field] ?? field}</Pill>{' '}
                    <span className="muted">「{result.quotes[field]}」</span>
                  </li>
                ))}
              </ul>
            </>
          )}
          <p className="muted small" style={{ marginBottom: 0 }}>
            読み取ったページ: {result.pages.join(' / ')}
          </p>
        </div>
      )}
    </div>
  );
}
